import { green, orange, red } from '@mui/material/colors'
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge'
import { TimeToXDto } from '../../../interfaces/IDashboard'
import { usePolling } from '../../../hooks/usePolling'
import InfoTooltip from './InfoTooltip'
import KpiBigNumber from './KpiBigNumber'

interface TimeToXCardProps {
  info?: string
}

const HISTORICAL_POLL_INTERVAL_MS = 5 * 60_000
const GAUGE_HEIGHT = 120

function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '–'
  if (minutes < 60) return `${Math.round(minutes)} min`
  if (minutes < 60 * 24) return `${(minutes / 60).toFixed(1)} t`
  return `${(minutes / (60 * 24)).toFixed(1)} d`
}

function slaColor(percent: number | null | undefined): string {
  if (percent === null || percent === undefined) return orange[700]
  if (percent >= 95) return green[700]
  if (percent >= 80) return orange[700]
  return red[700]
}

interface SlaGaugeProps {
  percent: number | null | undefined
  loaded: boolean
}

function SlaGauge({ percent, loaded }: SlaGaugeProps) {
  const hasValue = loaded && percent !== null && percent !== undefined
  const color = slaColor(percent)
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Lukket innen sluttid
      </Typography>
      {!loaded ? (
        <Skeleton variant="circular" width={GAUGE_HEIGHT} height={GAUGE_HEIGHT} />
      ) : (
        <Gauge
          width={GAUGE_HEIGHT * 1.6}
          height={GAUGE_HEIGHT}
          value={hasValue ? percent : 0}
          valueMin={0}
          valueMax={100}
          startAngle={-90}
          endAngle={90}
          innerRadius="70%"
          outerRadius="100%"
          cornerRadius={4}
          text={hasValue ? `${percent.toFixed(0)} %` : '–'}
          sx={{
            [`& .${gaugeClasses.valueArc}`]: { fill: color },
            [`& .${gaugeClasses.valueText}`]: { fontSize: 24, fontWeight: 700, fill: color },
          }}
        />
      )}
    </Box>
  )
}

function TimeToXCard({ info }: TimeToXCardProps) {
  const { data } = usePolling<TimeToXDto>('/api/stats/dashboard/time-to-x?window=30d', HISTORICAL_POLL_INTERVAL_MS)
  const loaded = data !== null

  return (
    <Paper elevation={1} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Reaksjonstid (30d)
        </Typography>
        {info && <InfoTooltip text={info} />}
      </Stack>
      <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, alignItems: 'center' }}>
        <KpiBigNumber label="Tid til berørte" value={loaded ? formatMinutes(data?.medianMinutesToAffected) : null} />
        <KpiBigNumber label="Tid til varsel" value={loaded ? formatMinutes(data?.medianMinutesToFirstAlert) : null} />
        <KpiBigNumber label="Tid til første handling" value={loaded ? formatMinutes(data?.medianMinutesToFirstAction) : null} />
        <SlaGauge percent={data?.slaCompliancePercent} loaded={loaded} />
      </Box>
    </Paper>
  )
}

export default TimeToXCard
